import * as React from "react";

import { Button, type ButtonProps } from "../button/button";
import { IconButton, type IconButtonProps } from "../icon-button/icon-button";
import { Input, type InputProps } from "../input/input";
import { Textarea, type TextareaProps } from "../textarea/textarea";
import { cn } from "../../lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";

const inputGroupVariants = cva(
  "group/input-group relative flex h-8 min-w-0 w-full items-center rounded-md border outline-none transition-[color,box-shadow,background-color,border-color] has-[>textarea]:h-auto has-[>textarea]:flex-col has-[>textarea]:items-stretch has-[[data-slot=input-group-control]:disabled]:opacity-50 data-[disabled=true]:opacity-50 has-[[data-slot=input-group-control][data-status=warning]]:border-warning has-[[data-slot=input-group-control][data-status=warning]]:[&_[data-slot=input-group-control]]:text-warning not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-status=warning]:focus-visible]:border-warning not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-status=warning]:focus-visible]:ring-warning/40 not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-status=warning][data-focus=true]]:border-warning not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-status=warning][data-focus=true]]:ring-warning/40 has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:[&_[data-slot=input-group-control]]:text-destructive not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][aria-invalid=true]:focus-visible]:border-destructive not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][aria-invalid=true]:focus-visible]:ring-destructive/20 not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][aria-invalid=true][data-focus=true]]:border-destructive not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][aria-invalid=true][data-focus=true]]:ring-destructive/20 not-has-[[data-slot=input-group-control][readonly]]:dark:has-[[data-slot=input-group-control][aria-invalid=true]:focus-visible]:ring-destructive/40 not-has-[[data-slot=input-group-control][readonly]]:dark:has-[[data-slot=input-group-control][aria-invalid=true][data-focus=true]]:ring-destructive/40",
  {
    variants: {
      variant: {
        default:
          "border-border bg-background shadow-xs not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control]:focus-visible]:border-control-focus not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control]:focus-visible]:ring-0 not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-focus=true]]:border-control-focus not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-focus=true]]:ring-0 dark:bg-input/30 has-[[data-slot=input-group-control][readonly]:enabled]:bg-transparent has-[[data-slot=input-group-control][readonly]:enabled]:shadow-none",
        ghost:
          "border-transparent bg-transparent shadow-none not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control]:focus-visible]:border-control-focus not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control]:focus-visible]:ring-0 not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-focus=true]]:border-control-focus not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-focus=true]]:ring-0 has-[[data-slot=input-group-control][readonly]]:ring-0! not-has-[[data-slot=input-group-control][readonly]]:hover:bg-accent not-has-[[data-slot=input-group-control][readonly]]:hover:[&_[data-slot=input-group-control]]:text-accent-foreground not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-hover=true]]:bg-accent not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-hover=true]]:[&_[data-slot=input-group-control]]:text-accent-foreground not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control]:focus-visible]:bg-background not-has-[[data-slot=input-group-control][readonly]]:has-[[data-slot=input-group-control][data-focus=true]]:bg-background has-[[data-slot=input-group-control][data-status=warning]]:bg-warning-foreground has-[[data-slot=input-group-control][aria-invalid=true]]:bg-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type InputGroupProps = React.ComponentProps<"div"> &
  VariantProps<typeof inputGroupVariants> & {
    asChild?: boolean;
  };

/**
 * Group contract: **state** styling (focus ring, invalid, disabled)
 * deep-matches `data-slot="input-group-control"` anywhere in the subtree.
 * **Spacing** (block-mode `pt-3`/`pb-3`) applies to direct-child controls
 * only — a composer that nests the control inside a wrapper owns that
 * wrapper's padding. In block mode the group also stretches all direct
 * children full-width (`items-stretch`), so a wrapper needs no `w-full`.
 */
function InputGroup({
  asChild = false,
  className,
  role,
  variant,
  ...props
}: InputGroupProps) {
  const Comp = asChild ? Slot.Root : "div";

  return (
    <Comp
      data-slot="input-group"
      data-variant={variant ?? "default"}
      role={role ?? (asChild ? undefined : "group")}
      className={cn(
        inputGroupVariants({ variant }),
        "has-[[data-align=inline-end]]:[&>[data-slot=input-group-control]]:pr-2 has-[[data-align=inline-start]]:[&>[data-slot=input-group-control]]:pl-1.5",
        "has-[[data-align=block-start]]:h-auto has-[[data-align=block-start]]:flex-col has-[[data-align=block-start]]:items-stretch has-[[data-align=block-start]]:[&>[data-slot=input-group-control]]:pb-3",
        "has-[[data-align=block-end]]:h-auto has-[[data-align=block-end]]:flex-col has-[[data-align=block-end]]:items-stretch has-[[data-align=block-end]]:[&>[data-slot=input-group-control]]:pt-3",
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start":
          "order-first pl-3 has-[[data-size=xs]]:py-1 has-[[data-size=sm]]:py-0.5",
        "inline-end":
          "order-last pr-3 has-[[data-size]]:pr-1.5 has-[[data-size=xs]]:py-1 has-[[data-size=sm]]:py-0.5",
        "block-start":
          "order-first w-full justify-between px-3 pt-3 group-has-[>[data-slot=input-group-control][type]]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end":
          "order-last w-full justify-between px-3 pb-3 group-has-[>[data-slot=input-group-control][type]]/input-group:pb-2.5 [.border-t]:pt-3",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

type InputGroupAddonProps = React.ComponentProps<"div"> &
  VariantProps<typeof inputGroupAddonVariants>;

const interactiveSelector =
  "button, a, input, textarea, select, [role=button], [role=link], [tabindex]:not([tabindex='-1'])";

function InputGroupAddon({
  align = "inline-start",
  className,
  onClick,
  ...props
}: InputGroupAddonProps) {
  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Pointer clicks on non-interactive addon content delegate focus to the existing keyboard-focusable control.
    <div
      data-align={align}
      data-slot="input-group-addon"
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(event) => {
        onClick?.(event);

        if (
          event.defaultPrevented ||
          (event.target instanceof Element &&
            event.target.closest(interactiveSelector))
        ) {
          return;
        }

        event.currentTarget
          .closest('[data-slot="input-group"]')
          ?.querySelector<HTMLElement>("[data-slot=input-group-control]")
          ?.focus();
      }}
      {...props}
    />
  );
}

type InputGroupActionSize = "xs" | "sm";

type DistributiveOmit<T, TKey extends PropertyKey> = T extends unknown
  ? Omit<T, TKey>
  : never;

type InputGroupButtonProps = Omit<ButtonProps, "children" | "size"> & {
  /** Visible action label. Use InputGroupIconButton for icon-only actions. */
  children: React.ReactNode;
  size?: InputGroupActionSize;
};

function InputGroupButton({
  className,
  size = "xs",
  type = "button",
  variant = "ghost",
  ...props
}: InputGroupButtonProps) {
  return (
    <Button
      type={type}
      data-size={size}
      size={size}
      variant={variant}
      className={cn("shadow-none", className)}
      {...props}
    />
  );
}

/** Icon-only Input Group action with the compact `xs` / `sm` size scale. */
type InputGroupIconButtonProps = DistributiveOmit<IconButtonProps, "size"> & {
  size?: InputGroupActionSize;
};

function InputGroupIconButton({
  size = "xs",
  ...props
}: InputGroupIconButtonProps) {
  return <IconButton {...props} data-size={size} size={size} />;
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="input-group-text"
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

type InputGroupInputProps = Omit<InputProps, "variant">;

function InputGroupInput({ className, ...props }: InputGroupInputProps) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        "flex-1 rounded-none border-0 bg-transparent shadow-none !ring-0 disabled:opacity-100 dark:bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

type InputGroupTextareaProps = Omit<TextareaProps, "variant">;

function InputGroupTextarea({ className, ...props }: InputGroupTextareaProps) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none !ring-0 disabled:opacity-100 dark:bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupIconButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
export type {
  InputGroupAddonProps,
  InputGroupButtonProps,
  InputGroupIconButtonProps,
  InputGroupInputProps,
  InputGroupProps,
  InputGroupTextareaProps,
};
